import { CheckCircleOutline, CloudDoneOutlined, LockOutlined, PublicOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { JSONContent } from '@tiptap/react'
import { FC, useCallback, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { DocPageDetailFragment, DocPageScope } from '../../../generated'
import { DOC_AUTOSAVE_DELAY, DOCS_LABELS } from './docs.constants'
import { RichTextEditor } from './editor/RichTextEditor'

interface DocPageEditorProps {
  page: DocPageDetailFragment
  publishing: boolean
  /** Admins/super-admins may flip a page between organization and private. */
  canSetVisibility: boolean
  onSaveDraft: (title: string, content: JSONContent) => Promise<void> | void
  onPublish: (title: string, content: JSONContent) => Promise<void> | void
  onChangeVisibility: (scope: DocPageScope) => Promise<void> | void
  onDone: () => void
}

type SaveState = 'idle' | 'saving' | 'saved'

export const DocPageEditor: FC<DocPageEditorProps> = ({
  page,
  publishing,
  canSetVisibility,
  onSaveDraft,
  onPublish,
  onChangeVisibility,
  onDone,
}) => {
  const [title, setTitle] = useState(page.title || '')
  const [saveState, setSaveState] = useState<SaveState>('idle')
  // Editor owns the live document; we keep the latest in a ref for publish.
  const contentRef = useRef<JSONContent>((page.draftContent || page.content || null) as JSONContent)
  const titleRef = useRef(title)

  const persistDraft = useDebouncedCallback(async () => {
    setSaveState('saving')
    await onSaveDraft(titleRef.current, contentRef.current)
    setSaveState('saved')
  }, DOC_AUTOSAVE_DELAY)

  const handleContentChange = useCallback(
    (json: JSONContent) => {
      contentRef.current = json
      setSaveState('saving')
      persistDraft()
    },
    [persistDraft],
  )

  const handleTitleChange = (value: string) => {
    setTitle(value)
    titleRef.current = value
    setSaveState('saving')
    persistDraft()
  }

  const handlePublish = async () => {
    persistDraft.cancel()
    await onPublish(titleRef.current, contentRef.current)
  }

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          {saveState === 'saving' && (
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
              {DOCS_LABELS.AUTOSAVING}
            </Typography>
          )}
          {saveState === 'saved' && (
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
              <CloudDoneOutlined fontSize="small" sx={{ mr: 0.5 }} />
              {DOCS_LABELS.SAVED}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {canSetVisibility && (
            <Tooltip
              title={
                page.scope === DocPageScope.Personal
                  ? DOCS_LABELS.VISIBILITY_PRIVATE_HINT
                  : DOCS_LABELS.VISIBILITY_ORG_HINT
              }
            >
              <ToggleButtonGroup
                value={page.scope}
                exclusive
                size="small"
                onChange={(_e, next) => next && next !== page.scope && onChangeVisibility(next)}
              >
                <ToggleButton value={DocPageScope.Org} sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
                  <PublicOutlined fontSize="small" sx={{ mr: 0.5 }} />
                  {DOCS_LABELS.VISIBILITY_ORG}
                </ToggleButton>
                <ToggleButton value={DocPageScope.Personal} sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
                  <LockOutlined fontSize="small" sx={{ mr: 0.5 }} />
                  {DOCS_LABELS.VISIBILITY_PRIVATE}
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
          <Button variant="text" color="inherit" onClick={onDone} sx={{ borderRadius: 2 }}>
            Done
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleOutline />}
            disabled={publishing}
            onClick={handlePublish}
            sx={{ borderRadius: 2, px: 2.5, boxShadow: 'none' }}
          >
            {DOCS_LABELS.PUBLISH}
          </Button>
        </Stack>
      </Stack>

      <TextField
        fullWidth
        variant="standard"
        placeholder={DOCS_LABELS.UNTITLED}
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        InputProps={{ disableUnderline: true, sx: { fontSize: 32, fontWeight: 700 } }}
        sx={{ mb: 2 }}
      />

      <RichTextEditor
        key={page.id}
        pageId={page.id}
        content={(page.draftContent || page.content || null) as JSONContent}
        onChange={handleContentChange}
      />
    </Box>
  )
}
